import waitForAccountToBeStarted from 'owa-account-source-list/lib/accountStateChanged/waitForAccountToBeStarted';
import { logGreyError } from 'owa-analytics';
import { lazyLoadItemCLPInfo, lazyGetAttachmentLabels } from 'owa-mail-protection';
import { getAccountScopeUserSettings } from 'owa-session-store/lib/selectors/getAccountScopeUserSettings';
import type { MailboxInfo } from 'owa-client-types';
import type AttachmentId from 'owa-service/lib/contract/AttachmentId';
import type { ClientItem } from 'owa-mail-store';
export interface CLPInfo {
    clpLabelId: string;
    tenantId: string;
}
let cachedCLPInfo: CLPInfo[] | undefined;
let currItemId: string | undefined;

const ACCOUNT_START_WAIT_TIMEOUT = 3000;

export async function getCLPInfo(
    itemId: string,
    mailboxInfo: MailboxInfo,
    attachmentIds: AttachmentId[] | undefined
): Promise<CLPInfo[] | undefined> {
    try {
        // If the attachmentIds are undefined, we cannot retrieve the CLP info accurately
        if (!attachmentIds) {
            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logGreyError('InaccurateCLPInfo_AttachmentIdsUndefined', undefined);
            return undefined;
        }

        // Use cached CLP info if available and the global item ID hasn't changed
        if (itemId === currItemId && cachedCLPInfo) {
            return cachedCLPInfo;
        }

        currItemId = itemId;

        // Fetch CLP info for the item
        const itemCLPInfo = await lazyLoadItemCLPInfo.importAndExecute(itemId);
        const clpLabelId = itemCLPInfo?.selectedLabel?.id;

        // Wait for the account to be started
        const isStarted = await waitForAccountToBeStarted(mailboxInfo, ACCOUNT_START_WAIT_TIMEOUT);
        if (!isStarted) {
            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logGreyError('ErrorLoadingClpInfo_AccountNotStarted', undefined);
            return undefined;
        }

        // Get the tenant ID from the mailbox info
        const tenantId =
            getAccountScopeUserSettings(mailboxInfo)?.SessionSettings?.ExternalDirectoryTenantGuid;

        // Attachment labels
        const attachmentClpLabelIds: string[] = [];
        if (attachmentIds.length > 0) {
            const getAttachmentLabels = await lazyGetAttachmentLabels.import();
            const attachmentLabelsReponse = await getAttachmentLabels(attachmentIds, mailboxInfo);
            if (
                !attachmentLabelsReponse ||
                !attachmentLabelsReponse.Body ||
                !attachmentLabelsReponse.Body.ResponseMessages ||
                !attachmentLabelsReponse.Body.ResponseMessages.Items ||
                attachmentLabelsReponse.Body.ResponseMessages.Items.length === 0
            ) {
                /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
                 * BASELINE: DO NOT COPY/PASTE!
                 *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
                logGreyError('InaccurateCLPInfo_GetAttachmentLabelsFailed', undefined);
                return undefined;
            }
            attachmentLabelsReponse.Body.ResponseMessages.Items.forEach(item => {
                if (item?.AttachmentLabel?.LabelId) {
                    const label = item.AttachmentLabel.LabelId;
                    attachmentClpLabelIds.push(label);
                }
            });
        }

        // Combine item and attachment label IDs
        const clpLabelIds: string[] = clpLabelId
            ? [clpLabelId, ...attachmentClpLabelIds]
            : [...attachmentClpLabelIds];

        if (clpLabelIds.length === 0) {
            // No CLP labels found
            return undefined;
        }

        if (!tenantId) {
            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logGreyError('InaccurateCLPInfo_TenantIdUndefined', undefined);
            return undefined;
        }

        // Map each labelId to { clpLabelId, tenantId }
        const clpInfoArray: CLPInfo[] = clpLabelIds.map(labelId => ({
            clpLabelId: labelId,
            tenantId,
        }));
        cachedCLPInfo = clpInfoArray;
        return clpInfoArray;
    } catch (error) {
        /* eslint-disable owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
        // @ts-expect-error
        logGreyError('ErrorLoadingClpInfo', undefined, error);
        /* eslint-enable owa-custom-rules/telemetry-mailboxinfo-as-second-param */
        return undefined;
    }
}

export function getAttachmentIds(item: ClientItem | undefined): AttachmentId[] | undefined {
    if (!item) {
        return undefined;
    }
    if (!item.Attachments) {
        return [];
    }
    if (item.Attachments.some(att => att.AttachmentId === undefined)) {
        return undefined;
    }
    return item.Attachments.map(att => att.AttachmentId as AttachmentId);
}
